import React, { useState, useEffect } from 'react';
import { User, CreditCard, Bell, Lock, Settings, AlertTriangle, Gift, Zap, Download, Users, Megaphone, Check, Shield, Leaf, Star, Rocket, Building2, Crown } from 'lucide-react';
import PhoneInput from '../components/PhoneInput';

const API = 'https://qbdp-backend-production.up.railway.app/api';

const card = {
  background: 'var(--white)', borderRadius: 12,
  border: '1px solid var(--border)', padding: '24px 28px',
};

const PLANS = [
  {
    id: 'starter', label: 'Starter', Icon: Leaf,
    color: '#0d9488', bg: '#f0fdfa', border: '#99f6e4',
    mensuel: 29, annuel: 24,
    employes: '0 — 25', comms: '500 emails/SMS',
    whitelabel: false,
  },
  {
    id: 'pro', label: 'Pro', Icon: Star,
    color: '#1a56db', bg: '#eff6ff', border: '#bfdbfe',
    mensuel: 79, annuel: 66,
    employes: '0 — 100', comms: '1 000 emails/SMS',
    whitelabel: true, popular: true,
  },
  {
    id: 'proplus', label: 'Pro+', Icon: Rocket,
    color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe',
    mensuel: 149, annuel: 124,
    employes: '0 — 200', comms: '2 500 emails/SMS',
    whitelabel: true,
  },
  {
    id: 'business', label: 'Business', Icon: Building2,
    color: '#d97706', bg: '#fffbeb', border: '#fde68a',
    mensuel: 249, annuel: 207,
    employes: '200 — 500', comms: 'Illimité',
    whitelabel: true,
  },
];

function Section({ title, subtitle, children }) {
  return (
    <div style={card}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontWeight: 600, fontSize: 15 }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: 'var(--gray)', marginTop: 3 }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', paddingBottom: 16, marginBottom: 16, borderBottom: '1px solid #f3f4f6' }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: '#1a1a2e' }}>{label}</div>
        {hint && <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 2 }}>{hint}</div>}
      </div>
      <div style={{ marginLeft: 32, flexShrink: 0 }}>{children}</div>
    </div>
  );
}

function Toggle({ on, onChange }) {
  return (
    <div onClick={onChange} style={{
      width: 40, height: 22, borderRadius: 11, cursor: 'pointer',
      background: on ? 'var(--blue)' : '#d1d5db',
      position: 'relative', transition: 'background 0.2s',
    }}>
      <div style={{
        position: 'absolute', top: 3, left: on ? 21 : 3,
        width: 16, height: 16, borderRadius: '50%', background: 'white',
        transition: 'left 0.2s',
      }} />
    </div>
  );
}

function Input({ value, onChange, type = 'text', placeholder }) {
  return (
    <input type={type} value={value} onChange={onChange} placeholder={placeholder}
      style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 13, outline: 'none', width: 240, background: 'white' }} />
  );
}

function SaveBtn({ onClick, saved }) {
  return (
    <button onClick={onClick} style={{
      background: saved ? '#166534' : 'var(--blue)', color: 'white', border: 'none',
      borderRadius: 8, padding: '9px 20px', fontSize: 13, fontWeight: 500, cursor: 'pointer', transition: 'background 0.3s',
      display: 'flex', alignItems: 'center', gap: 6,
    }}>
      {saved ? <><Check size={13} /> Enregistré</> : 'Enregistrer'}
    </button>
  );
}

// ── BANNIÈRE ABONNEMENT ────────────────────────────────────────────────────────
function BanniereAbonnement({ abo }) {
  if (!abo) return null;
  const token = sessionStorage.getItem('qbdp_token');

  const handlePayer = async () => {
    const r = await fetch(`${API}/stripe-enterprise/portail`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }
    });
    const d = await r.json();
    if (d.url) window.location.href = d.url;
  };

  if (abo.statut === 'trial') {
    if (abo.jours_restants > 3) return null; // Pas de bannière si > 3 jours
    return (
      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: '12px 20px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 13, color: '#1e40af', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Gift size={14} /> <strong>Essai gratuit</strong> — {abo.jours_restants} jour{abo.jours_restants > 1 ? 's' : ''} restant{abo.jours_restants > 1 ? 's' : ''}
        </div>
        <button onClick={() => document.getElementById('tab-abonnement')?.click()}
          style={{ background: '#1a56db', color: 'white', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          Choisir un plan →
        </button>
      </div>
    );
  }

  if (abo.statut === 'retard') return (
    <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: '12px 20px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ fontSize: 13, color: '#92400e', display: 'flex', alignItems: 'center', gap: 6 }}>
        <AlertTriangle size={14} /> <strong>Paiement en attente</strong> — Régularisez votre abonnement pour éviter une suspension
      </div>
      <button onClick={handlePayer} style={{ background: '#d97706', color: 'white', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
        Payer maintenant
      </button>
    </div>
  );

  if (abo.statut === 'grace') return (
    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: '14px 20px', marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#991b1b', display: 'flex', alignItems: 'center', gap: 6 }}>
          <AlertTriangle size={15} /> Abonnement expiré — {abo.jours_restants} jour{abo.jours_restants > 1 ? 's' : ''} pour régulariser
        </div>
        <button onClick={handlePayer} style={{ background: '#dc2626', color: 'white', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          Renouveler maintenant
        </button>
      </div>
      <div style={{ fontSize: 12, color: '#dc2626', opacity: 0.8 }}>
        Après cette période : accès BLE bloqués, personnalisation réinitialisée, dashboard en lecture seule uniquement.
        Vos données sont conservées et exportables.
      </div>
    </div>
  );

  if (abo.statut === 'suspendu') return (
    <div style={{ background: '#1a1a2e', border: '1px solid #374151', borderRadius: 12, padding: '20px 24px', marginBottom: 20 }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: 'white', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}><Lock size={16} /> Abonnement suspendu</div>
      <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 16 }}>
        Vos accès BLE sont bloqués et la personnalisation a été réinitialisée. Vous pouvez toujours exporter vos données.
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={handlePayer} style={{ background: '#1a56db', color: 'white', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          <Zap size={14} /> Reprendre l'abonnement
        </button>
        <button style={{ background: 'transparent', color: '#9ca3af', border: '1px solid #374151', borderRadius: 8, padding: '10px 20px', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Download size={14} /> Exporter mes données CSV
        </button>
      </div>
    </div>
  );

  return null;
}

// ── ONGLET ABONNEMENT ──────────────────────────────────────────────────────────
function OngletAbonnement() {
  const [abo, setAbo]           = useState(null);
  const [loading, setLoading]   = useState(true);
  const [facturation, setFact]  = useState('mensuel');
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const token = sessionStorage.getItem('qbdp_token');
  const savedUser = JSON.parse(sessionStorage.getItem('qbdp_user') || '{}');
  const isSuperAdmin = savedUser.role === 'super_admin' || savedUser.email === 'oliviergueroult@qbdp.fr';

  useEffect(() => {
    fetch(`${API}/stripe-enterprise/statut`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setAbo(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleCheckout = async (planId) => {
    setCheckoutLoading(planId);
    try {
      const r = await fetch(`${API}/stripe-enterprise/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan: planId, facturation })
      });
      const d = await r.json();
      if (d.url) window.location.href = d.url;
    } catch { alert('Erreur lors de la redirection vers le paiement'); }
    setCheckoutLoading(null);
  };

  const handlePortail = async () => {
    const r = await fetch(`${API}/stripe-enterprise/portail`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }
    });
    const d = await r.json();
    if (d.url) window.location.href = d.url;
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Chargement...</div>;

  const superAdminBanner = isSuperAdmin ? (
    <div style={{ borderRadius: 16, overflow: 'hidden', border: '2px solid #fbbf24', boxShadow: '0 8px 32px rgba(251,191,36,0.2)' }}>
        <div style={{ background: 'linear-gradient(135deg, #1e1b4b, #312e81)', padding: '28px 32px', display: 'flex', alignItems: 'center', gap: 20 }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: 'rgba(251,191,36,0.15)', border: '2px solid #fbbf24', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Crown size={28} color="#fbbf24" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#fbbf24', letterSpacing: 0.5 }}>Connexion Super Administration</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>{savedUser.email}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ background: '#fbbf24', color: '#1e1b4b', fontSize: 11, fontWeight: 800, padding: '4px 14px', borderRadius: 20, letterSpacing: 0.5 }}>ILLIMITÉ</div>
          </div>
        </div>
        <div style={{ background: '#1e1b4b', borderTop: '1px solid rgba(251,191,36,0.2)', padding: '16px 32px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          {[
            { label: 'Employés', value: 'Illimité' },
            { label: 'Clients / Patients', value: 'Illimité' },
            { label: 'Emails / SMS', value: 'Illimité' },
          ].map(s => (
            <div key={s.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#fbbf24', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                ∞ {s.value}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 3 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>
  ) : null;

  const planActuel = PLANS.find(p => p.id === abo?.plan);

  const statutLabel = {
    trial:    { label: 'Essai gratuit', bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
    actif:    planActuel
      ? { label: 'Actif', bg: planActuel.bg, color: planActuel.color, border: planActuel.border }
      : { label: 'Actif', bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
    retard:   { label: 'Paiement en attente', bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
    grace:    { label: 'Période de grâce',    bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
    suspendu: { label: 'Suspendu',      bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb' },
  }[abo?.statut] || { label: '—', bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {superAdminBanner}

      {/* Statut actuel */}
      {!isSuperAdmin && <div style={card}>
        <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 16 }}>Abonnement actuel</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', background: statutLabel.bg, border: `1px solid ${statutLabel.border}`, borderRadius: 12, marginBottom: 16 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: statutLabel.color }}>
              {abo?.statut === 'trial' ? 'Essai gratuit — 15 jours' : `Plan ${planActuel?.label || 'Actif'}`}
            </div>
            <div style={{ fontSize: 12, color: statutLabel.color, opacity: 0.8, marginTop: 3 }}>
              {abo?.statut === 'trial' && abo?.trial_ends_at && `Essai jusqu'au ${new Date(abo.trial_ends_at).toLocaleDateString('fr-FR')} — ${abo.jours_restants !== null ? `${abo.jours_restants} jour${abo.jours_restants > 1 ? 's' : ''} restant${abo.jours_restants > 1 ? 's' : ''}` : ''}`}
              {abo?.statut === 'actif' && abo?.current_period_end && `Renouvellement le ${new Date(abo.current_period_end).toLocaleDateString('fr-FR')}`}
              {abo?.statut === 'grace' && abo?.jours_restants !== null && `${abo.jours_restants} jour${abo.jours_restants > 1 ? 's' : ''} pour régulariser`}
              {abo?.statut === 'suspendu' && 'Compte suspendu — renouvelez pour continuer'}
            </div>
          </div>
          <span style={{ background: statutLabel.color, color: 'white', fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 10 }}>
            {statutLabel.label}
          </span>
        </div>

        {/* Date d'achat / début */}
        {(abo?.activated_at || abo?.created_at) && (
          <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 14 }}>
            {abo?.statut === 'trial'
              ? `Essai démarré le ${new Date(abo.created_at).toLocaleDateString('fr-FR')}`
              : `Abonnement souscrit le ${new Date(abo.activated_at || abo.created_at).toLocaleDateString('fr-FR')}`
            }
          </div>
        )}

        {/* Actions selon statut */}
        <div style={{ display: 'flex', gap: 10 }}>
          {(abo?.statut === 'actif') && (
            <button onClick={handlePortail} style={{ background: 'white', color: '#1a56db', border: '2px solid #bfdbfe', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              <CreditCard size={14} /> Gérer la facturation
            </button>
          )}
          {(abo?.statut === 'suspendu' || abo?.statut === 'grace' || abo?.statut === 'retard') && (
            <button onClick={handlePortail} style={{ background: '#1a56db', color: 'white', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Zap size={14} /> Reprendre l'abonnement
            </button>
          )}
          {(abo?.statut === 'suspendu' || abo?.statut === 'grace') && (
            <button style={{ background: 'white', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 8, padding: '9px 18px', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Download size={14} /> Exporter données CSV
            </button>
          )}
        </div>
      </div>}

      {/* Choisir / changer de plan */}
      {abo?.statut !== 'actif' || true ? (
        <div style={card}>
          <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6 }}>
            {abo?.statut === 'trial' ? 'Choisir votre plan' : 'Changer de plan'}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 20 }}>15 jours d'essai gratuit inclus</div>

          {/* Toggle mensuel/annuel */}
          <div style={{ display: 'inline-flex', background: '#f3f4f6', borderRadius: 10, padding: 4, gap: 4, marginBottom: 24 }}>
            {['mensuel', 'annuel'].map(f => (
              <button key={f} onClick={() => setFact(f)}
                style={{ padding: '7px 16px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  background: facturation === f ? 'white' : 'transparent',
                  color: facturation === f ? '#1a1a2e' : '#6b7280',
                  boxShadow: facturation === f ? '0 1px 4px rgba(0,0,0,0.1)' : 'none' }}>
                {f === 'mensuel' ? 'Mensuel' : 'Annuel'} {f === 'annuel' && <span style={{ color: '#16a34a', fontSize: 11 }}>-17%</span>}
              </button>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            {PLANS.map(p => {
              const isCurrent = abo?.plan === p.id && ['actif', 'trial'].includes(abo?.statut);
              const prix = facturation === 'mensuel' ? p.mensuel : p.annuel;
              return (
                <div key={p.id} style={{
                  border: `2px solid ${p.color}`,
                  borderRadius: 14, padding: '18px 16px', position: 'relative',
                  background: p.bg,
                  display: 'flex', flexDirection: 'column',
                }}>
                  {p.popular && !isCurrent && <div style={{ position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)', background: p.color, color: 'white', fontSize: 10, fontWeight: 700, padding: '2px 10px', borderRadius: 10, whiteSpace: 'nowrap' }}>Populaire</div>}
                  {isCurrent && <div style={{ position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)', background: p.color, color: 'white', fontSize: 10, fontWeight: 700, padding: '2px 10px', borderRadius: 10, whiteSpace: 'nowrap' }}>✓ Actuel</div>}
                  <p.Icon size={18} color={p.color} />
                  <div style={{ fontWeight: 700, fontSize: 14, marginTop: 6, color: '#1a1a2e' }}>{p.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: p.color, margin: '6px 0 2px' }}>{prix}€</div>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 10 }}>/ mois{facturation === 'annuel' ? ' (annuel)' : ''}</div>
                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}><Users size={11} /> {p.employes} emp.</div>
                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 4 }}><Megaphone size={11} /> {p.comms}</div>
                  {p.whitelabel && <div style={{ fontSize: 11, color: '#16a34a', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 4 }}><Check size={11} /> White label</div>}
                  <button onClick={() => handleCheckout(p.id)} disabled={isCurrent || checkoutLoading === p.id}
                    style={{ marginTop: 'auto', width: '100%', padding: '9px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: isCurrent ? 'default' : 'pointer',
                      background: isCurrent || p.popular ? p.color : 'white',
                      color: isCurrent || p.popular ? 'white' : p.color,
                      border: `2px solid ${p.color}` }}>
                    {isCurrent ? '✓ Plan actuel' : checkoutLoading === p.id ? 'Chargement...' : 'Choisir'}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Enterprise */}
          <div style={{ marginTop: 12, padding: '14px 18px', background: '#1a1a2e', borderRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ color: 'white', fontWeight: 600, fontSize: 13 }}>Enterprise — 500+ employés</div>
              <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 2 }}>Multi-sites, API REST, SSO, SLA 99.9%, account manager dédié</div>
            </div>
            <a href="mailto:contact@qbdp.fr" style={{ background: 'white', color: '#1a1a2e', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer', textDecoration: 'none' }}>
              Nous contacter
            </a>
          </div>
        </div>
      ) : null}

    </div>
  );
}

// ── COMPOSANT PRINCIPAL ────────────────────────────────────────────────────────
export default function Reglages() {
  const [activeTab, setActiveTab] = useState('compte');
  const [saved, setSaved]         = useState(false);
  const [abo, setAbo]             = useState(null);
  const token = sessionStorage.getItem('qbdp_token');

  useEffect(() => {
    fetch(`${API}/stripe-enterprise/statut`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setAbo).catch(() => {});
  }, []);

const handleSave = async () => {
  try {
    const token = sessionStorage.getItem('qbdp_token');
    const r = await fetch(`${API}/auth/me`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ nom, telephone: tel })
    });
    const d = await r.json();
    if (d.id) {
      sessionStorage.setItem('qbdp_user', JSON.stringify(d));
      setSaved(true);
setTimeout(() => window.location.reload(), 1000);
      setTimeout(() => setSaved(false), 2000);
    }
  } catch (err) { console.error(err); }
};

  const savedUser = JSON.parse(sessionStorage.getItem('qbdp_user') || '{}');
  const [nom, setNom]     = useState(savedUser.nom || '');
  const [email, setEmail] = useState(savedUser.email || '');
  const [tel, setTel]     = useState(savedUser.telephone || '');

  const [notifs, setNotifs] = useState({
    acces_refuse: true, acces_bloque: true, nouvel_employe: true,
    rapport_hebdo: false, alerte_sms: false, alerte_email: true,
  });
  const toggleNotif = k => setNotifs(n => ({ ...n, [k]: !n[k] }));

  const [double_auth, setDoubleAuth]             = useState(true);
  const [session_duree, setSessionDuree]         = useState('8h');
  const [journal_connexions, setJournalConnexions] = useState(true);

  const [modules, setModules] = useState({
    detection_proximite: false, mode_visiteur: true, api_externe: false,
  });
  const toggleModule = k => setModules(m => ({ ...m, [k]: !m[k] }));

  const tabs = [
    { id: 'compte',        label: 'Compte',        Icon: User },
    { id: 'abonnement',    label: 'Abonnement',    Icon: CreditCard },
    { id: 'notifications', label: 'Notifications', Icon: Bell },
    { id: 'securite',      label: 'Sécurité',      Icon: Shield },
    { id: 'modules',       label: 'Modules',       Icon: Settings },
    { id: 'danger',        label: 'Zone de danger',Icon: AlertTriangle },
  ];

  return (
    <div style={{ padding: '28px 32px' }}>

      {/* Bannière abonnement */}
      <BanniereAbonnement abo={abo} />

      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 20, fontWeight: 600 }}>Réglages</h1>
        <p style={{ fontSize: 13, color: 'var(--gray)', marginTop: 4 }}>Gérez votre compte et la configuration qbdp</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 20 }}>

        {/* Menu latéral */}
        <div style={{ ...card, padding: '12px 0', alignSelf: 'start' }}>
          {tabs.map(t => (
            <div key={t.id} id={`tab-${t.id}`} onClick={() => setActiveTab(t.id)} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 18px', cursor: 'pointer', fontSize: 13,
              color: activeTab === t.id ? 'var(--blue)' : '#374151',
              background: activeTab === t.id ? 'var(--blue-light)' : 'transparent',
              borderLeft: activeTab === t.id ? '3px solid var(--blue)' : '3px solid transparent',
              fontWeight: activeTab === t.id ? 500 : 400,
              transition: 'all 0.15s',
            }}>
              <t.Icon size={14} color={activeTab === t.id ? 'var(--blue)' : '#6b7280'} />
              {t.label}
              {t.id === 'abonnement' && abo && ['retard','grace','suspendu'].includes(abo.statut) && (
                <span style={{ marginLeft: 'auto', width: 8, height: 8, borderRadius: '50%', background: '#dc2626' }} />
              )}
            </div>
          ))}
        </div>

        {/* Contenu */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* COMPTE */}
          {activeTab === 'compte' && (
            <>
              <Section title="Informations du compte" subtitle="Vos informations d'administrateur principal">
                <Field label="Nom complet" hint="Affiché dans le dashboard et les rapports">
                  <Input value={nom} onChange={e => setNom(e.target.value)} />
                </Field>
                <Field label="Adresse e-mail" hint="Utilisée pour la connexion et les alertes">
                  <Input value={email} onChange={e => setEmail(e.target.value)} type="email" />
                </Field>
                <Field label="Téléphone" hint="Votre numéro personnel pour recevoir les alertes SMS">
                  <PhoneInput value={tel} onChange={setTel} />
                </Field>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                  <SaveBtn onClick={handleSave} saved={saved} />
                </div>
              </Section>
            </>
          )}

          {/* ABONNEMENT */}
          {activeTab === 'abonnement' && <OngletAbonnement />}

          {/* NOTIFICATIONS */}
          {activeTab === 'notifications' && (
            <Section title="Notifications" subtitle="Choisissez quand et comment être alerté">
              <Field label="Accès refusé" hint="Alerte immédiate si un badge inconnu tente d'entrer">
                <Toggle on={notifs.acces_refuse} onChange={() => toggleNotif('acces_refuse')} />
              </Field>
              <Field label="Accès bloqué" hint="Quand un employé suspendu tente d'accéder">
                <Toggle on={notifs.acces_bloque} onChange={() => toggleNotif('acces_bloque')} />
              </Field>
              <Field label="Nouvel employé ajouté" hint="Confirmation lors de l'ajout d'un compte">
                <Toggle on={notifs.nouvel_employe} onChange={() => toggleNotif('nouvel_employe')} />
              </Field>
              <Field label="Rapport hebdomadaire" hint="Résumé envoyé chaque lundi matin">
                <Toggle on={notifs.rapport_hebdo} onChange={() => toggleNotif('rapport_hebdo')} />
              </Field>
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 4 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--gray)', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Canaux</div>
                <Field label="Alertes par e-mail" hint={email}>
                  <Toggle on={notifs.alerte_email} onChange={() => toggleNotif('alerte_email')} />
                </Field>
                <Field label="Alertes par SMS" hint={tel || 'Aucun numéro configuré'}>
                  <Toggle on={notifs.alerte_sms} onChange={() => toggleNotif('alerte_sms')} />
                </Field>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                <SaveBtn onClick={handleSave} saved={saved} />
              </div>
            </Section>
          )}

          {/* SÉCURITÉ */}
          {activeTab === 'securite' && (
            <>
              <Section title="Authentification" subtitle="Sécurisez l'accès au dashboard admin">
                <Field label="Double authentification (2FA)" hint="Code envoyé par SMS ou application à chaque connexion">
                  <Toggle on={double_auth} onChange={() => setDoubleAuth(v => !v)} />
                </Field>
                <Field label="Durée de session" hint="Déconnexion automatique après inactivité">
                  <select value={session_duree} onChange={e => setSessionDuree(e.target.value)}
                    style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 13, outline: 'none' }}>
                    <option value="1h">1 heure</option>
                    <option value="4h">4 heures</option>
                    <option value="8h">8 heures</option>
                    <option value="24h">24 heures</option>
                  </select>
                </Field>
                <Field label="Journal des connexions" hint="Enregistrer chaque connexion admin dans l'historique">
                  <Toggle on={journal_connexions} onChange={() => setJournalConnexions(v => !v)} />
                </Field>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                  <SaveBtn onClick={handleSave} saved={saved} />
                </div>
              </Section>
              <Section title="Changer le mot de passe" subtitle="Utilisez un mot de passe fort d'au moins 12 caractères">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {[{ label: 'Mot de passe actuel', ph: '••••••••••••' }, { label: 'Nouveau mot de passe', ph: 'Min. 12 caractères' }, { label: 'Confirmer le nouveau', ph: 'Répéter le mot de passe' }].map(f => (
                    <div key={f.label} style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <div style={{ width: 200, fontSize: 13, color: '#374151' }}>{f.label}</div>
                      <input type="password" placeholder={f.ph} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 13, outline: 'none', width: 240 }} />
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                    <button style={{ background: 'var(--blue)', color: 'white', border: 'none', borderRadius: 8, padding: '9px 20px', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>Mettre à jour</button>
                  </div>
                </div>
              </Section>
            </>
          )}

          {/* MODULES */}
          {activeTab === 'modules' && (
            <Section title="Modules qbdp" subtitle="Activez ou désactivez les fonctionnalités de votre plan">
              <Field label="Détection de proximité" hint="Ouverture automatique quand un véhicule approche à 1m">
                <Toggle on={modules.detection_proximite} onChange={() => toggleModule('detection_proximite')} />
              </Field>
              <Field label="Mode visiteur" hint="Accès temporaire pour les visiteurs et prestataires">
                <Toggle on={modules.mode_visiteur} onChange={() => toggleModule('mode_visiteur')} />
              </Field>
              <Field label="API externe" hint="Connectez qbdp à vos outils RH ou ERP via API REST">
                <Toggle on={modules.api_externe} onChange={() => toggleModule('api_externe')} />
              </Field>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                <SaveBtn onClick={handleSave} saved={saved} />
              </div>
            </Section>
          )}

          {/* DANGER */}
          {activeTab === 'danger' && (
            <Section title="Zone de danger" subtitle="Actions irréversibles — procédez avec précaution">
              {[
                { label: 'Réinitialiser tous les accès', desc: 'Supprime toutes les autorisations accordées aux employés. Les comptes sont conservés.', btn: 'Réinitialiser les accès', color: 'var(--amber)', bg: 'var(--amber-light)', border: '#fde68a' },
                { label: 'Exporter toutes les données', desc: 'Téléchargez l\'ensemble des données (employés, accès, logs) au format CSV.', btn: 'Exporter tout (CSV)', color: 'var(--blue)', bg: 'var(--blue-light)', border: '#bfdbfe' },
                { label: 'Supprimer le compte', desc: 'Supprime définitivement votre compte et toutes les données associées. Cette action est irréversible.', btn: 'Supprimer le compte', color: 'var(--red)', bg: 'var(--red-light)', border: '#fecaca' },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '16px 18px', borderRadius: 10, background: item.bg, border: `1px solid ${item.border}`, marginBottom: 12 }}>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 13, color: item.color }}>{item.label}</div>
                    <div style={{ fontSize: 12, color: item.color, opacity: 0.8, marginTop: 3, maxWidth: 380 }}>{item.desc}</div>
                  </div>
                  <button style={{ background: 'white', color: item.color, border: `1px solid ${item.border}`, borderRadius: 8, padding: '7px 16px', fontSize: 12, fontWeight: 500, cursor: 'pointer', flexShrink: 0, marginLeft: 16 }}>
                    {item.btn}
                  </button>
                </div>
              ))}
            </Section>
          )}

        </div>
      </div>
    </div>
  );
}